// DOM building blocks shared by the task drawer and the approvals queue: draft editor (rendered
// Markdown ↔ autosized textarea), reject form, files gallery + lightbox, agent chip, sections,
// collapsible text, copy to clipboard. Text always goes in as text nodes; agent Markdown through
// renderMarkdown() only.
import {
  h, button, iconButton, badge, field, textarea, switchToggle, monoLabel, icon, setAttr, setText, classNames,
} from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { renderMarkdown, formatBytes } from '../../format.js';
import { agentAvatar } from '../agent/avatar.js';
import {
  buildApproveBody, buildRejectBody, validateRejection, parseJsonDraft, prettyJson, isImageFile, fileUrl, inputFileUrl,
  clipText, MAX_FEEDBACK, MAX_LESSON,
} from './model.js';
import './strings.js';

/** requestAnimationFrame when available (browser), else a short timeout (tests). */
export function raf(fn) {
  return typeof globalThis.requestAnimationFrame === 'function' ? globalThis.requestAnimationFrame(fn) : setTimeout(fn, 16);
}

/**
 * Makes a textarea grow with its content (up to `max` px). Returns the fit function.
 * @param {HTMLTextAreaElement} area
 * @param {{max?: number, min?: number}} [opts]
 */
export function autosize(area, { max = 560, min = 0 } = {}) {
  const fit = () => {
    area.style.setProperty('height', 'auto');
    const sh = Number(area.scrollHeight) || 0;
    if (!sh) { area.style.removeProperty('height'); return; }
    area.style.setProperty('height', `${Math.max(min, Math.min(sh + 2, max))}px`);
  };
  area.addEventListener('input', fit);
  return fit;
}

/**
 * While `active()` is true, Escape pressed inside `el` runs `onEscape` instead of closing the
 * layer (the overlay listens on document in the capture phase; this listens on window first).
 * @returns {() => void} remover
 */
export function guardEscape(el, active, onEscape) {
  const win = globalThis.window;
  if (!win?.addEventListener) return () => {};
  const fn = (e) => {
    if (e.key !== 'Escape' || e.target !== el || !active()) return;
    e.stopPropagation();
    e.preventDefault();
    onEscape();
  };
  win.addEventListener('keydown', fn, true);
  return () => win.removeEventListener('keydown', fn, true);
}

/**
 * Copies text to the clipboard and says so in a toast.
 * @returns {Promise<boolean>}
 */
export async function copyText(ctx, text) {
  try {
    if (globalThis.navigator?.clipboard?.writeText) {
      await globalThis.navigator.clipboard.writeText(String(text));
      ctx.toast?.success?.(t('common.copied'));
      return true;
    }
  } catch { /* fall back below */ }
  try {
    const doc = globalThis.document;
    const area = h('textarea', { class: 'sr-only', readOnly: true, value: String(text), 'aria-hidden': 'true' });
    doc.body.appendChild(area);
    area.select?.();
    const ok = typeof doc.execCommand === 'function' && doc.execCommand('copy');
    area.remove();
    if (ok) { ctx.toast?.success?.(t('common.copied')); return true; }
  } catch { /* nothing else to try */ }
  ctx.toast?.error?.(t('td.copyFailed'));
  return false;
}

/**
 * A titled section (mono label + optional actions on the right).
 * @param {string} label
 * @param {Node|Node[]} content
 * @param {{actions?: Node|Node[], class?: string, id?: string}} [opts]
 */
export function section(label, content, opts = {}) {
  return h('section', { class: classNames(['td-sec', opts.class]), 'aria-label': label },
    h('div', { class: 'td-sec-head' }, monoLabel(label, { class: 'td-sec-title' }), opts.actions ? h('div', { class: 'td-sec-actions' }, opts.actions) : null),
    content);
}

/**
 * A text shown clipped with an "Afficher tout" toggle when it is long.
 * @param {string} text
 * @param {{maxChars?: number, maxLines?: number, class?: string}} [opts]
 */
export function collapsibleText(text, { maxChars = 600, maxLines = 8, class: cls = 'td-text' } = {}) {
  const full = String(text ?? '');
  const clip = clipText(full, { maxChars, maxLines });
  const body = h('p', { class: 'td-text-body' }, clip.text);
  if (!clip.truncated) return h('div', { class: cls }, body);
  let open = false;
  const label = h('span', { class: 'btn-label' }, t('td.showMore'));
  const toggle = h('button', {
    type: 'button', class: 'btn btn-link btn-sm td-text-toggle', 'aria-expanded': 'false',
    onClick: () => {
      open = !open;
      body.textContent = open ? full : clip.text;
      setText(label, open ? t('td.showLess') : t('td.showMore'));
      setAttr(toggle, 'aria-expanded', String(open));
    },
  }, label);
  return h('div', { class: cls }, body, toggle);
}

/**
 * The agent of a task: small portrait + NAME, a button opening the agent drawer.
 * @param {object} ctx
 * @param {object|null} agent
 * @param {{name?: string}} [opts] fallback name (task.agentName) when the agent is not visible
 */
export function agentChip(ctx, agent, { name } = {}) {
  if (!agent) {
    return h('span', { class: 'td-agent is-unknown' }, h('span', { class: 'td-agent-name' }, name || t('td.agentRouting')));
  }
  return h('button', {
    type: 'button', class: 'td-agent', title: agent.role || null,
    'aria-label': t('td.agentOpen', { name: agent.name }),
    onClick: () => ctx.navigate(`#/agent/${encodeURIComponent(agent.id)}`),
  }, agentAvatar(agent, { size: 22 }), h('span', { class: 'td-agent-name' }, agent.name));
}

/** A read-only view of a draft/result: pretty JSON in a code block, or rendered Markdown. */
export function renderDeliverable(text, outputFormat) {
  const value = String(text ?? '');
  if (outputFormat === 'json' && parseJsonDraft(value).ok) {
    return h('pre', { class: 'td-code' }, h('code', {}, prettyJson(value)));
  }
  // showHrefs: every link of a draft or result shows its whole address (the text of a link written
  // by an agent could imitate another address, or hide data in the URL).
  return h('div', { class: 'md td-md' }, renderMarkdown(value, { showHrefs: true }));
}

/**
 * The draft of a task awaiting approval: rendered Markdown by default, an autosized textarea when
 * editing. Escape inside the textarea goes back to the preview instead of closing the drawer.
 * `toggleInHead: false` leaves the MODIFIER / APERÇU button out of the draft's head (`toggleBtn`, to
 * be placed by the caller: the approval box puts it in its action bar); `toggleKey` shows its key.
 * @param {{text?: string, outputFormat?: string, label?: string, onChange?: Function, toggleInHead?: boolean, toggleKey?: string}} opts
 * @returns {{el: HTMLElement, toggleBtn: HTMLElement, value(): string, original: string, isDirty(): boolean, isEditing(): boolean,
 *   setEditing(on: boolean, o?: {focus?: boolean}): void, toggle(): void, reset(): void, setError(msg: string|null): void,
 *   focus(): void, approveBody(): object, destroy(): void}}
 */
export function createDraftEditor({ text = '', outputFormat = 'text', label = t('td.section.draft'), onChange, toggleInHead = true, toggleKey = '' } = {}) {
  const original = String(text ?? '');
  let editing = false;
  const view = h('div', { class: 'td-draft-view' });
  const area = textarea({
    class: 'td-draft-area', value: original, rows: 10, spellcheck: true, hidden: true,
    'aria-label': t('td.approval.editLabel'),
  });
  const fit = autosize(area, { max: 640, min: 160 });
  const edited = badge(t('td.approval.edited'), 'amber');
  edited.hidden = true;
  const revert = h('button', { type: 'button', class: 'btn btn-link btn-sm td-draft-revert', hidden: true, onClick: () => reset() },
    h('span', { class: 'btn-label' }, t('td.approval.revert')));
  const toggleLabel = h('span', { class: 'btn-label' }, t('td.approval.edit'));
  const toggleBtn = h('button', {
    type: 'button', class: ['btn btn-secondary td-draft-toggle', { 'btn-sm': toggleInHead }], 'aria-pressed': 'false', onClick: () => api.toggle(),
    'aria-keyshortcuts': toggleKey || null,
  }, icon('eye', { size: 14 }), toggleLabel, toggleKey ? h('kbd', { class: 'kbd td-kbd', 'aria-hidden': 'true' }, toggleKey) : null);
  const error = h('p', { class: 'td-draft-error', role: 'alert', hidden: true });
  const hint = h('p', { class: 'td-draft-hint', hidden: true }, t('td.approval.escHint'));
  const el = h('div', { class: 'td-draft', dataset: { format: outputFormat || 'text' } },
    h('div', { class: 'td-draft-head' }, monoLabel(label, { class: 'td-sec-title' }), edited, h('span', { class: 'spacer' }), revert, toggleInHead ? toggleBtn : null),
    view, area, hint, error);

  const value = () => (typeof area.value === 'string' ? area.value : original);
  const isDirty = () => buildApproveBody(original, value()).editedDraft !== undefined;
  const renderView = () => view.replaceChildren(value().trim() ? renderDeliverable(value(), outputFormat) : h('p', { class: 'muted' }, t('td.approval.noDraft')));
  const sync = () => {
    const dirty = isDirty();
    edited.hidden = !dirty;
    revert.hidden = !dirty;
    el.classList.toggle('is-dirty', dirty);
  };
  const setError = (msg) => {
    setText(error, msg || '');
    error.hidden = !msg;
    if (msg) area.setAttribute('aria-invalid', 'true'); else area.removeAttribute('aria-invalid');
  };
  function setEditing(on, { focus = false } = {}) {
    editing = !!on;
    area.hidden = !editing;
    hint.hidden = !editing;
    view.hidden = editing;
    el.classList.toggle('is-editing', editing);
    setText(toggleLabel, editing ? t('td.approval.preview') : t('td.approval.edit'));
    setAttr(toggleBtn, 'aria-pressed', String(editing));
    if (editing) {
      raf(fit);
      if (focus) raf(() => area.focus?.({ preventScroll: false }));
    } else {
      renderView();
    }
  }
  function reset() {
    area.value = original;
    setError(null);
    sync();
    if (!editing) renderView();
    fit();
    onChange?.(api);
  }
  area.addEventListener('input', () => { setError(null); sync(); onChange?.(api); });
  const offEsc = guardEscape(area, () => editing, () => { setEditing(false); toggleBtn.focus?.(); });
  renderView();

  const api = {
    el, original, value, isDirty, reset, setError, toggleBtn,
    isEditing: () => editing,
    setEditing,
    toggle() { setEditing(!editing, { focus: !editing }); },
    focus() { (editing ? area : toggleBtn).focus?.(); },
    approveBody: () => buildApproveBody(original, value()),
    destroy() { offEsc(); },
  };
  return api;
}

/**
 * The reject form: feedback + "Retenir comme règle pour cet agent" + "Refaire avec ce retour".
 * onSubmit receives the POST body; a thrown error is shown under the feedback field.
 * `allowRemember: false` (below manage: the server refuses it) hides the "Retenir" switch.
 * @param {{onSubmit: (body: object) => Promise<void>|void, onCancel?: () => void, allowRemember?: boolean}} opts
 * @returns {{el: HTMLElement, focus(): void, setBusy(busy: boolean): void, isDirty(): boolean}}
 */
export function createRejectForm({ onSubmit, onCancel, allowRemember = true } = {}) {
  const area = textarea({
    class: 'td-reject-text', rows: 4, maxLength: MAX_FEEDBACK, placeholder: t('td.reject.placeholder'), autofocus: true,
  });
  autosize(area, { max: 320 });
  const f = field(t('approval.feedback'), area, { hint: t('td.reject.hint') });
  const remember = switchToggle(t('approval.remember'), { checked: false, hint: t('td.reject.rememberHint'), onChange: () => sync() });
  const rerun = switchToggle(t('td.reject.rerunLabel'), { checked: true, hint: t('td.reject.rerunHint'), onChange: () => sync() });
  const cut = h('p', { class: 'td-reject-note', hidden: true }, icon('info', { size: 14 }), h('span', {}, t('td.reject.lessonCut')));
  const submitLabel = h('span', { class: 'btn-label' }, t('td.reject.submitRerun'));
  const submitBtn = h('button', { type: 'submit', class: 'btn btn-primary td-reject-submit' }, submitLabel);
  const cancelBtn = onCancel ? button(t('common.cancel'), { variant: 'ghost', onClick: () => onCancel() }) : null;
  let busy = false;

  const values = () => ({ feedback: area.value ?? '', remember: allowRemember && !!remember.input.checked, rerun: !!rerun.input.checked });
  function sync() {
    const v = values();
    setText(submitLabel, v.rerun ? t('td.reject.submitRerun') : t('td.reject.submitFinal'));
    cut.hidden = !(v.remember && String(v.feedback).trim().length > MAX_LESSON);
  }
  function setBusy(on) {
    busy = !!on;
    submitBtn.disabled = busy;
    submitBtn.classList.toggle('is-busy', busy);
    if (cancelBtn) cancelBtn.disabled = busy;
  }
  async function submit(e) {
    e?.preventDefault?.();
    if (busy) return;
    const v = values();
    const err = validateRejection(v);
    if (err) { f.setError(err); area.focus?.(); return; }
    f.setError(null);
    setBusy(true);
    try {
      await onSubmit?.(buildRejectBody(v));
    } catch (error) {
      f.setError(error?.message || t('common.error'));
    } finally {
      setBusy(false);
    }
  }
  area.addEventListener('input', () => { f.setError(null); sync(); });
  area.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(e);
  });
  const el = h('form', { class: 'td-reject', novalidate: true, onSubmit: submit },
    f, h('div', { class: 'td-reject-opts' }, allowRemember ? remember : null, rerun), cut,
    h('div', { class: 'td-reject-actions' }, cancelBtn, submitBtn));
  sync();
  return { el, focus: () => area.focus?.(), setBusy, isDirty: () => !!String(area.value ?? '').trim(), submit };
}

/**
 * Opens an image in a modal with previous / next (buttons and ← →) and a download link.
 * @param {object} ctx
 * @param {Array<{name: string, url: string, size?: number}>} images
 * @param {number} [start]
 * @returns {object} the modal layer
 */
export function openLightbox(ctx, images, start = 0) {
  if (!images.length) return null;
  let i = Math.max(0, Math.min(images.length - 1, start));
  const img = h('img', { class: 'td-lb-img', alt: '', decoding: 'async' });
  const caption = h('figcaption', { class: 'td-lb-caption' });
  const counter = h('span', { class: 'td-lb-count mono-label', 'aria-live': 'polite' });
  const dlLabel = h('span', { class: 'btn-label' }, t('taskdrawer.download'));
  const dl = h('a', { class: 'btn btn-secondary btn-sm', href: images[i].url, download: images[i].name }, dlLabel);
  const many = images.length > 1;
  const prev = many ? iconButton('chevronLeft', t('td.files.prev'), { onClick: () => show(i - 1) }) : null;
  const next = many ? iconButton('chevronRight', t('td.files.next'), { onClick: () => show(i + 1) }) : null;
  let layer = null;
  function show(n) {
    i = (n + images.length) % images.length;
    const f = images[i];
    setAttr(img, 'src', f.url);
    setAttr(img, 'alt', f.name);
    setText(caption, f.size ? `${f.name} · ${formatBytes(f.size)}` : f.name);
    setText(counter, many ? t('td.files.lightbox', { i: i + 1, n: images.length }) : '');
    setAttr(dl, 'href', f.url);
    setAttr(dl, 'download', f.name);
    setAttr(dl, 'aria-label', t('td.files.download', { name: f.name }));
    layer?.setTitle?.(f.name);
  }
  layer = ctx.overlay.openModal({
    title: images[i].name, size: 'lg', className: 'td-lightbox',
    content: h('figure', { class: 'td-lb' }, img, caption),
    footer: [prev, counter, next, h('span', { class: 'spacer' }), dl].filter(Boolean),
  });
  const pop = ctx.keys?.pushScope?.((e) => {
    if (ctx.overlay.top?.() !== layer || !many) return false;
    if (e.key === 'ArrowLeft') { show(i - 1); return true; }
    if (e.key === 'ArrowRight') { show(i + 1); return true; }
    return false;
  });
  if (pop) layer.onClose(pop);
  show(i);
  return layer;
}

/** Is this a video the browser can play inline (mp4, webm, mov)? */
export function isVideoFile(f) {
  const mime = String(f?.mime || '').toLowerCase();
  const name = String(f?.name || '').toLowerCase();
  return mime.startsWith('video/') || /\.(mp4|webm|mov|m4v)$/.test(name);
}

/**
 * The input files of a task (« Fichiers fournis », SPEC §16): a playable player for every video,
 * thumbnails for images, a download row for the rest. Every file has its own url
 * (/api/tasks/:id/files/in/<name>).
 * @param {object} ctx
 * @param {string} taskId
 * @param {Array<{name: string, size?: number, mime?: string, url?: string}>} files
 */
export function inputFilesGallery(ctx, taskId, files) {
  const list = (Array.isArray(files) ? files : []).filter((f) => f && typeof f.name === 'string' && f.name)
    .map((f) => ({ ...f, url: inputFileUrl(taskId, f) }));
  const videos = list.filter(isVideoFile);
  const rest = list.filter((f) => !isVideoFile(f));
  const players = videos.length ? h('ul', { class: 'td-videos' }, videos.map((f) => h('li', { class: 'td-video' },
    h('video', { class: 'td-video-player', controls: true, preload: 'metadata', playsinline: true, src: f.url, 'aria-label': f.name }),
    h('div', { class: 'td-video-meta' },
      icon('film', { size: 14 }),
      h('span', { class: 'td-file-name', title: f.name }, f.name),
      f.size ? h('span', { class: 'td-file-size' }, formatBytes(f.size)) : null,
      h('a', { class: 'btn btn-ghost btn-sm', href: f.url, download: f.name, 'aria-label': t('td.files.download', { name: f.name }) },
        h('span', { class: 'btn-label' }, t('taskdrawer.download'))))))) : null;
  return h('div', { class: 'td-gallery td-inputs' }, players, rest.length ? filesGallery(ctx, taskId, rest, { urlOf: (f) => inputFileUrl(taskId, f) }) : null);
}

/**
 * The files of a task: image thumbnails (click → lightbox) and a list of the other files with
 * download links.
 * @param {object} ctx
 * @param {string} taskId
 * @param {Array<{name: string, size?: number, mime?: string, url?: string}>} files
 * @param {{compact?: boolean, urlOf?: (file: object) => string}} [opts] urlOf: the URL builder (output files by default)
 */
export function filesGallery(ctx, taskId, files, opts = {}) {
  const { compact = false } = opts;
  const list = (Array.isArray(files) ? files : []).filter((f) => f && typeof f.name === 'string' && f.name);
  const urlOf = opts.urlOf || ((f) => fileUrl(taskId, f));
  const images = list.filter(isImageFile).map((f) => ({ ...f, url: urlOf(f) }));
  const others = list.filter((f) => !isImageFile(f)).map((f) => ({ ...f, url: urlOf(f) }));
  const grid = images.length ? h('ul', { class: classNames(['td-thumbs', { 'is-compact': compact }]) }, images.map((f, idx) => {
    const pic = h('img', { src: f.url, alt: f.name, loading: 'lazy', decoding: 'async' });
    pic.addEventListener('error', () => pic.closest?.('.td-thumb')?.classList.add('is-broken'));
    return h('li', { class: 'td-thumb-item' },
      h('button', { type: 'button', class: 'td-thumb', 'aria-label': t('td.files.open', { name: f.name }), onClick: () => openLightbox(ctx, images, idx) },
        pic),
      compact ? null : h('div', { class: 'td-thumb-meta' },
        h('span', { class: 'td-thumb-name', title: f.name }, f.name),
        h('a', { class: 'td-file-dl', href: f.url, download: f.name, 'aria-label': t('td.files.download', { name: f.name }), title: t('taskdrawer.download') },
          icon('arrowRight', { size: 14, class: 'td-dl-icon' }))));
  })) : null;
  const rows = others.length ? h('ul', { class: 'td-files' }, others.map((f) => h('li', { class: 'td-file' },
    h('span', { class: 'td-file-icon', 'aria-hidden': 'true' }, icon('layers', { size: 16 })),
    h('span', { class: 'td-file-name', title: f.name }, f.name),
    f.size ? h('span', { class: 'td-file-size' }, formatBytes(f.size)) : null,
    h('a', { class: 'btn btn-ghost btn-sm', href: f.url, download: f.name, 'aria-label': t('td.files.download', { name: f.name }) },
      h('span', { class: 'btn-label' }, t('taskdrawer.download')))))) : null;
  return h('div', { class: 'td-gallery' }, grid, rows);
}
